import { StandardEvents, CartDiscountUpdateEvent } from '@shopify/events';

// PDP coupon (snippets/skullette-coupon-strip.liquid). States: idle -> pending
// -> claimed | error, plus 'other' when the cart already carries a different
// code (never removed: /discount/X appends, it does not evict).
// Unclaimed shows the parchment ticket; claimed hides it and turns the
// buy-head price into code price + struck list + "✓ 40% off applied" chip,
// with "You save $X · comes off at checkout · remove" under it.
//
// Claim = GET /discount/CODE, then /cart.js must list it in discount_codes
// (GET /discount/<anything> answers 200 even for a dead code). A local flag
// remembers the claim; every page load reconciles it against /cart.js: code
// missing -> re-attach once; still missing -> forget it. Nothing is applied
// without that first tap. Remove = /cart/update.js with every other code kept.

const STRIP = '[data-coupon-strip]';
const TIMEOUT_MS = 8000;
const money = (cents) => `$${(cents / 100).toFixed(2)}`;
const same = (a, b) => typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();
const inFlight = new Set();
const reattachTried = new Set();

// localStorage throws in some private modes; the coupon must keep working.
const flagKey = (code) => `skullette:coupon:${code.toLowerCase()}`;
const store = {
  has: (code) => { try { return localStorage.getItem(flagKey(code)) === '1'; } catch { return false; } },
  set: (code) => { try { localStorage.setItem(flagKey(code), '1'); } catch { /* page-life only */ } },
  drop: (code) => { try { localStorage.removeItem(flagKey(code)); } catch { /* nothing stored */ } },
};

async function fetchCart() {
  try {
    const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
}

const cartCodes = (cart) => (Array.isArray(cart?.discount_codes) ? cart.discount_codes : []).map((d) => d?.code).filter(Boolean);

// { attached, other } from cart.discount_codes (present even on an empty cart).
function attachment(cart, code) {
  const codes = cartCodes(cart);
  if (codes.some((c) => same(c, code))) return { attached: true, other: '' };
  return { attached: false, other: codes[0] || '' };
}

// Mirrors the Liquid: fixed cents, else floor(price x pct / 100); capped at price.
function savingFor(strip, price) {
  const fixed = parseInt(strip.dataset.fixedCents || '0', 10);
  const pct = parseFloat(strip.dataset.discountPct || '0');
  return Math.min(fixed > 0 ? fixed : pct > 0 ? Math.floor((price * pct) / 100) : 0, price);
}

// Claimed: the buy-head headline = code price, list struck, applied chip.
// Otherwise the list price comes back. A clearance variant keeps its own
// compare-at strike. The list cents are parked on the headline because
// skullette-pdp.js prices the ATC label from the list, not the headline.
function paintHeadPrice(strip, claimed, price, saving) {
  const wrap = strip.closest('.sk-buy-head')?.querySelector('.sk-buy-head__price');
  const nowEl = wrap?.querySelector('.sk-buy-head__now');
  if (!wrap || !nowEl) return;
  if (claimed) {
    const wasEl = wrap.querySelector('.sk-buy-head__was');
    nowEl.textContent = money(price - saving);
    nowEl.dataset.listCents = String(price);
    if (wasEl && !wrap.classList.contains('is-clear')) wasEl.textContent = money(price);
    let chip = wrap.querySelector('.sk-claim__chip');
    if (!chip) {
      chip = document.createElement('span');
      chip.className = 'sk-claim__chip';
      wrap.append(chip);
    }
    chip.textContent = `✓ ${strip.dataset.offLabel || ''} off applied`;
    wrap.classList.add('sk-has-code');
  } else if (wrap.classList.contains('sk-has-code')) {
    nowEl.textContent = money(price);
    delete nowEl.dataset.listCents;
    wrap.classList.remove('sk-has-code');
  }
}

function paint(strip) {
  const state = strip.dataset.state || 'idle';
  const price = parseInt(strip.dataset.priceCents || '0', 10);
  const saving = parseInt(strip.dataset.savingsCents || '0', 10);
  const sub = strip.querySelector('[data-coupon-sub]');
  const cta = strip.querySelector('[data-coupon-cta]');
  const applied = strip.querySelector('[data-coupon-applied]');
  const btn = strip.querySelector('[data-coupon-strip-btn]');
  if (!sub || !cta || !applied || !btn) return;

  const pay = document.createElement('b');
  pay.textContent = money(price - saving);
  sub.replaceChildren('Pay ', pay, ` instead of ${money(price)}.`);
  const saved = document.createElement('b');
  saved.textContent = money(saving);
  applied.replaceChildren('You save ', saved, ' · comes off at checkout · ');
  cta.textContent = state === 'pending' ? 'Adding…'
    : state === 'error' ? 'Try again'
    : state === 'other' ? `Tap to add — ${strip.dataset.otherCode} stays`
    : 'Tap to apply coupon';
  btn.disabled = state === 'pending' || state === 'claimed';
  btn.setAttribute('aria-busy', String(state === 'pending'));
  paintHeadPrice(strip, state === 'claimed', price, saving);
}

function setState(code, state, other = '') {
  for (const strip of document.querySelectorAll(STRIP)) {
    if (!same(strip.dataset.couponCode, code)) continue;
    if (other) strip.dataset.otherCode = other;
    else delete strip.dataset.otherCode;
    strip.dataset.state = state;
    paint(strip);
  }
}

// Apply what the cart says. reattach=true is the page-load path: a remembered
// claim the cart lost gets one silent re-attach before it is forgotten.
async function sync(code, cart, reattach) {
  if (!cart) return;
  const att = attachment(cart, code);
  if (att.attached) {
    store.set(code);
    setState(code, 'claimed');
    return;
  }
  if (reattach && store.has(code) && !att.other && !reattachTried.has(code)) {
    reattachTried.add(code);
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
    } catch { /* judged by the cart below */ }
    const fresh = await fetchCart();
    if (fresh && attachment(fresh, code).attached) {
      store.set(code);
      setState(code, 'claimed');
      refreshCartUi(fresh);
      return;
    }
  }
  store.drop(code);
  setState(code, att.other ? 'other' : 'idle', att.other);
}

// Horizon's cart drawer re-renders on a cart discount update raised outside it
// (component-cart-items.js #handleDiscountUpdate -> renderSection).
function refreshCartUi(cart) {
  if (!cart?.item_count) return;
  const deferred = CartDiscountUpdateEvent.createPromise();
  document.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: cartCodes(cart).map((code) => ({ code })), promise: deferred.promise }));
  deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart) });
}

// Tap to apply (state guards keep a double tap from firing twice).
async function claim(strip) {
  const code = strip.dataset.couponCode;
  if (!code || inFlight.has(code) || ['pending', 'claimed'].includes(strip.dataset.state)) return;
  inFlight.add(code);
  const other = strip.dataset.otherCode || '';
  setState(code, 'pending', other);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin', signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const cart = await fetchCart();
    if (!cart || !attachment(cart, code).attached) throw new Error('code not attached');
    store.set(code);
    setState(code, 'claimed');
    refreshCartUi(cart);
  } catch (error) {
    console.warn('[coupon-strip] claim failed:', error);
    setState(code, 'error', other);
  } finally {
    clearTimeout(timer);
    inFlight.delete(code);
  }
}

// The "remove" link: same as the cart chip x - every other code stays.
async function remove(strip) {
  const code = strip.dataset.couponCode;
  if (!code || inFlight.has(code)) return;
  inFlight.add(code);
  store.drop(code);
  try {
    const keep = cartCodes(await fetchCart()).filter((c) => !same(c, code));
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: keep.join(',') }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch (error) {
    console.warn('[coupon-strip] remove failed:', error);
  } finally {
    inFlight.delete(code);
  }
  const cart = await fetchCart();
  await sync(code, cart, false);
  refreshCartUi(cart);
}

async function initAll() {
  const strips = [...document.querySelectorAll(STRIP)].filter((s) => !s.dataset.couponInit);
  if (!strips.length) return;
  for (const strip of strips) {
    strip.dataset.couponInit = '1';
    if (store.has(strip.dataset.couponCode)) strip.dataset.state = 'claimed';
    paint(strip);
  }
  const cart = await fetchCart();
  for (const code of new Set(strips.map((s) => s.dataset.couponCode).filter(Boolean))) await sync(code, cart, true);
}

if (!window.__skCouponStripInit) {
  window.__skCouponStripInit = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const strip = target?.closest(STRIP);
    if (!strip) return;
    if (target.closest('[data-coupon-strip-btn]')) { event.preventDefault(); claim(strip); }
    else if (target.closest('[data-coupon-remove]')) { event.preventDefault(); remove(strip); }
  });

  // Variant switch: skullette-pdp.js hands every strip in the buy box the
  // selected variant's list price; saving, "Pay" and the headline follow it.
  document.addEventListener('skullette:coupon:price', (event) => {
    const strip = event.target.closest?.(STRIP);
    const price = Number(event.detail?.price);
    if (!strip || !Number.isFinite(price) || price <= 0) return;
    strip.dataset.priceCents = String(price);
    strip.dataset.savingsCents = String(savingFor(strip, price));
    paint(strip);
  });

  // The cart's chip x or any other discount change: follow the real cart,
  // without re-attaching what the shopper just removed.
  document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => {
    Promise.resolve(event.promise).catch(() => null).then(fetchCart).then((cart) => {
      const codes = new Set([...document.querySelectorAll(STRIP)].map((s) => s.dataset.couponCode).filter(Boolean));
      for (const code of codes) if (!inFlight.has(code)) sync(code, cart, false);
    });
  });

  // The size switcher swaps <main> for another product; pick up its strip.
  document.addEventListener('skullette:pdp:rebind', initAll);
}

initAll();
